import { create } from "zustand";
import { apiFetch } from "@/lib/api-fetch";
import { stabilizeProject } from "@/lib/store/stabilize-project";
import type { DirectorPlan } from "@/lib/ai/director-plan";

interface Character {
  id: string;
  name: string;
  speakerKind?: string;
  speakerIdentity?: string | null;
  soundSource?: string | null;
  description: string;
  referenceImage: string | null;
  referenceImageHistory?: string | null;
  /** 立绘：四视图的源图（两步出图第一步产物） */
  portraitImage?: string | null;
  visualHint?: string | null;
  scope?: string;
  episodeId?: string | null;
}

export interface Scene {
  id: string;
  projectId: string;
  episodeId?: string | null;
  title: string;
  description: string;
  lighting: string;
  colorPalette: string;
  imagePrompt: string;
  referenceImage: string | null;
  referenceImageHistory: string;
  sceneType: string;
  status: string;
  sequence: number;
}

interface Dialogue {
  id: string;
  text: string;
  characterId: string;
  characterName: string;
  sequence: number;
  /** 手改情绪（JSON 字符串，含 dim/strength/alpha/pace） */
  emoOverride?: string | null;
  /** 当前选中（合成用）的音频指针 */
  audioUrl?: string | null;
  audioDurationMs?: number | null;
  /**
   * 台词音频多版本（2026-10-23）。
   * `isActive=1` 的那一条就是 `audioUrl` 指向的版本 —— 合成只认指针，
   * 所以「保留多个版本、但只能选一个」是同一个事实的两种说法。
   */
  audioTakes?: Array<{
    id: string;
    url: string;
    durationMs: number | null;
    takeNo: number;
    isActive: number;
    createdAt: number;
    emoBasis: string | null;
  }>;
}

/**
 * One row from the unified `shot_assets` table, exposed to the frontend.
 * type discriminates the role:
 *   - 'first_frame' / 'last_frame'  → keyframe-mode image assets
 *   - 'reference'                   → reference-mode image assets (multi)
 *   - 'keyframe_video'              → keyframe-mode video output
 *   - 'reference_video'             → reference-mode video output
 */
/**
 * 客户端镜头资产类型。
 *
 * 🔴 2026-10-17（老板报「外网加载不到视频封面」）根因补齐：这里**漏了 `video_cover`**。
 *   `video_cover` 是后端一直在写的一类活跃资产（`db/schema.ts` 的枚举里有、
 *   `lib/shot-asset-utils.ts` 的 `ShotAssetType` 也有，出片时 ffmpeg 抽首帧落库，
 *   全库 112 行），但客户端类型把它排除了 —— 于是前端即使拿到这行数据也"看不见这个类型"，
 *   视频位只能挂着**没有 poster** 的 `<video>`，metadata 到货前一直空白，
 *   外网慢链路下表现就是"封面加载不到"。
 *   类型是事实的下游，不是事实的来源：补上它，而不是在组件里 `as any` 绕过去。
 */
export type ShotAssetType =
  | "first_frame"
  | "last_frame"
  | "reference"
  | "keyframe_video"
  | "reference_video"
  | "video_cover";

export interface ShotAsset {
  id: string;
  shotId: string;
  type: ShotAssetType;
  sequenceInType: number;
  assetVersion: number;
  isActive: number;
  prompt: string;
  fileUrl: string | null;
  status: "pending" | "generating" | "completed" | "failed";
  characters: string[] | null;
  modelProvider?: string | null;
  modelId?: string | null;
  meta?: { sceneName?: string } | null;
}

export interface Shot {
  id: string;
  sequence: number;
  /** 所属集 / 分镜版本（🔴 2026-10-18 顶开关联动需要按集+版本过滤本集镜头） */
  episodeId?: string | null;
  versionId?: string | null;
  /** 剧本场次号（剧本「场景 N」的 N）——单场重跑 sceneNumbers 的点名依据 */
  sceneNumber?: number | null;
  prompt: string;
  videoScript: string | null;
  motionScript: string | null;
  cameraDirection: string;
  duration: number;
  sceneId?: string;
  transitionIn?: string;
  transitionOut?: string;
  /** 🔴 2026-10-20：起止状态运镜（中文词，词表见 lib/ai/camera-movement-vocab） */
  startCamera?: string;
  endCamera?: string;
  /** 运镜时序（JSON：一镜内多节拍各走不同运镜） */
  cameraTimeline?: string;
  videoPrompt: string | null;
  /**
   * 🔴 2026-10-27（C 档）：编译当前 `videoPrompt` 时用的**分镜描述指纹**。
   * 与前端现算的指纹不一致 ⇒ 提示词已过期（判据唯一真源 `lib/ai/prompt-source-hash`）。
   * 空串 = 加列之前编译的 / 从没编译过 ⇒ 不算过期（不制造噪音）。
   */
  promptSourceHash?: string | null;
  /** 🔴 2026-10-27（C 档）：生成当前视频时用的**提示词指纹**（`textHash(videoPrompt)`）。 */
  videoSourceHash?: string | null;
  /**
   * 🔴 2026-10-14：冲击力加强开关（身体构图镜头库），**单镜级**。
   * 对应 DB 列 `shots.impact_boost_enabled`（schema 已声明）。
   * 0=关闭 1=开启 -1=继承项目级（`projects.impact_boost_enabled`）。
   * ⚠️ 与项目级同名不同层级：单镜可覆盖项目设置，UI 两处都在读它。
   */
  impactBoostEnabled?: number;
  /** Shot-local opt-in style; independent from the legacy global impact boost. */
  stylePackId?: string;
  stylePackLocked?: number;
  stylePackIntent?: string;
  compositionGuide?: string;
  focalPoint?: string;
  depthOfField?: string;
  soundDesign?: string;
  musicCue?: string;
  /**
   * 🔴 2026-10-17（迁移 0072）：情绪/表演/切镜职责四字段。
   *
   * 此前它们**只存在于 shot-split 的输出 schema 里**：模型写、系统丢，全库无第二处引用。
   * 现在落库了 —— 这是让「情绪弧」第一次有载体的一步（先只落不判，攒够真实分布再定判据）。
   * 口径：emotionBase=情绪基点（动机+对象+藏什么）/ emotionPeak=爆发点（秒点+肢体爆发）/
   * bodyPerformance=大动作+生活化小动作 / cutRole=起拍|爆发拍|反应拍|插入拍|收拍。
   */
  emotionBase?: string;
  emotionPeak?: string;
  bodyPerformance?: string;
  cutRole?: string;
  /**
   * 🔴 2026-10-17（迁移 0073）：动机锚 —— 情绪的存在条件。
   *
   * 情绪是「目标受阻」的副产品，不是形容词。没有这四栏，emotionBase→emotionPeak 只能套情绪模板，
   * 演得再用力也与剧情脱钩；没有 emotionTrigger，情绪变化无法解释。
   * objective=此刻想要什么 / obstacle=谁或什么在挡 / adjustment=受阻后换的策略 /
   * emotionTrigger=本镜情绪变化由哪句台词或哪个动作触发。
   */
  objective?: string;
  obstacle?: string;
  adjustment?: string;
  emotionTrigger?: string;
  qualityScore?: number;
  qualityIssues?: string[];
  isStale?: boolean;
  status: string;
  dialogues: Dialogue[];
  /** Active shot_assets rows for this shot, all types mixed. */
  assets: ShotAsset[];
}

// ─── Asset access helpers (use these in UI instead of legacy fields) ─────
// All helpers are null-safe — accept any object that may or may not have an
// `assets` field, falling back to empty array. Necessary because in-flight
// optimistic updates and partial fetches may produce shot objects without
// the assets field populated.

type ShotLike = { assets?: ShotAsset[] | null };

function safeAssets(shot: ShotLike): ShotAsset[] {
  return Array.isArray(shot?.assets) ? shot.assets : [];
}

/** Active assets only — `isActive === 1`. Use this for "current" reads. */
function activeAssets(shot: ShotLike): ShotAsset[] {
  return safeAssets(shot).filter((a) => a.isActive === 1);
}

/**
 * All version history rows for one slot (shot, type, sequenceInType),
 * sorted newest first by assetVersion. Use this to render history arrows.
 */
export function getAssetHistoryForSlot(
  shot: ShotLike,
  type: ShotAssetType,
  sequenceInType = 0
): ShotAsset[] {
  return safeAssets(shot)
    .filter((a) => a.type === type && a.sequenceInType === sequenceInType)
    .sort((a, b) => b.assetVersion - a.assetVersion);
}

/** Get the active first_frame image URL for a shot, or null. */
export function getFirstFrameUrl(shot: ShotLike): string | null {
  return (
    activeAssets(shot).find(
      (a) => a.type === "first_frame" && a.sequenceInType === 0
    )?.fileUrl ?? null
  );
}

/** Get the active last_frame image URL for a shot, or null. */
export function getLastFrameUrl(shot: ShotLike): string | null {
  return (
    activeAssets(shot).find(
      (a) => a.type === "last_frame" && a.sequenceInType === 0
    )?.fileUrl ?? null
  );
}

/** Get the keyframe-mode video URL, or null. */
export function getKeyframeVideoUrl(shot: ShotLike): string | null {
  return (
    activeAssets(shot).find(
      (a) => a.type === "keyframe_video" && a.sequenceInType === 0
    )?.fileUrl ?? null
  );
}

/** Get the reference-mode video URL, or null. */
export function getReferenceVideoUrl(shot: ShotLike): string | null {
  return (
    activeAssets(shot).find(
      (a) => a.type === "reference_video" && a.sequenceInType === 0
    )?.fileUrl ?? null
  );
}

/** Get all active reference image assets ordered by sequence_in_type. */
export function getReferenceAssets(shot: ShotLike): ShotAsset[] {
  return activeAssets(shot)
    .filter((a) => a.type === "reference")
    .sort((a, b) => a.sequenceInType - b.sequenceInType);
}

/** First reference image URL (used as the "scene reference frame"). */
export function getSceneRefFrameUrl(shot: ShotLike): string | null {
  return getReferenceAssets(shot)[0]?.fileUrl ?? null;
}

/** First-frame prompt text (the LLM-generated description). */
export function getFirstFramePrompt(shot: ShotLike): string | null {
  return (
    activeAssets(shot).find(
      (a) => a.type === "first_frame" && a.sequenceInType === 0
    )?.prompt ?? null
  );
}

/** Last-frame prompt text. */
export function getLastFramePrompt(shot: ShotLike): string | null {
  return (
    activeAssets(shot).find(
      (a) => a.type === "last_frame" && a.sequenceInType === 0
    )?.prompt ?? null
  );
}

/** Whether all reference images for a shot have been generated (have file_url). */
export function hasAllReferenceImages(shot: ShotLike): boolean {
  const refs = getReferenceAssets(shot);
  return refs.length > 0 && refs.every((r) => !!r.fileUrl);
}

/** Whether the shot has both first and last frame image URLs. */
export function hasKeyframePair(shot: ShotLike): boolean {
  return !!getFirstFrameUrl(shot) && !!getLastFrameUrl(shot);
}

export type StoryboardVersion = {
  id: string;
  label: string;
  versionNum: number;
  /** 所属集 —— 版本表是项目级的，前端必须按集过滤才能选对默认版本 */
  episodeId?: string | null;
  createdAt: number;
  /**
   * 🔴 2026-10-25（A 切镜策略轮换）：这一版是按哪种「怎么切」的依据切的。
   * 由 `/api/projects/[id]` 从 appSettings 的记录回读 —— 存后端不回读的话，
   * 用户刷新一次就再也看不出「这几版到底哪里不同」，轮换就白做了。
   */
  cutStrategy?: string | null;
};

interface Project {
  id: string;
  title: string;
  idea: string;
  script: string;
  outline?: string;
  status: string;
  finalVideoUrl: string | null;
  generationMode: "keyframe" | "reference";
  artStyle: string;
  aspectRatio: string;
  /** 视觉风格设定六件套（2026-09-18，剧本页设定卡） */
  colorPalette?: string;
  eraAesthetics?: string;
  mood?: string;
  referenceDirector?: string;
  /** 1/0：视觉风格锁定（锁定后重新生成剧本不再改写 §1 视觉风格） */
  styleLocked?: number;
  /**
   * 🔴 2026-10-14：冲击力加强开关（身体构图镜头库）。
   * 对应 DB 列 `projects.impact_boost_enabled`（schema 已加，迁移 0071）。
   * ⚠️ 之前漏加这一行 ⇒ `project.impactBoostEnabled` 编译期报 TS2339，
   *    且与「DB 缺列」叠加成整站 500。**改 schema 列时前端类型必须同步加。**
   */
  impactBoostEnabled?: number;
  /** Episode target duration in seconds; 0 = unset and blocks new Director V2 versions. */
  targetDuration?: number;
  /** dialogue | compact | artistic; empty means the user has not chosen a rhythm. */
  pacingProfile?: string;
  pacingEstimate?: { targetDuration: number; beats: number; estimatedShots: number } | null;
  /** Director-first pipeline and its script-bound plan (null until first V2 generation). */
  directorV2?: boolean;
  directorPlan?: DirectorPlan | null;
  /** true means the cached plan belongs to an older script and will be rebuilt. */
  directorPlanStale?: boolean;
  /** Manual per-scene style authorization. Empty/missing = unrestricted, never auto-apply. */
  styleScopes?: Record<string, string[]>;
  /**
   * 🔴 2026-10-25：**本版默认风格**（跟着分镜版本走，不是锁在镜头号上）。
   *
   * 由 `VersionStyleBar` 读取后写入，供镜头卡显示「跟随本版：X」用。
   * 镜头卡不能只看 `shot.stylePackId` —— 空串在新模型里意思是「跟随本版默认」，
   * 直接显示成「默认叙事」会让用户以为整版没设风格，然后重复设置。
   */
  versionStyle?: {
    versionId: string;
    stylePackId: string;
    stylePackIntent: string;
    total: number;
    overrides: number;
    inheriting: number;
  } | null;
  characters: Character[];
  scenes: Scene[];
  shots: Shot[];
  versions: StoryboardVersion[];
}

interface ProjectStore {
  project: Project | null;
  loading: boolean;
  /** 后台静默刷新中（有旧数据时的重新拉取）。用于顶部细进度条，不阻塞界面。 */
  refreshing: boolean;
  /**
   * 🔴 2026-10-06：拉取失败的错误信息。
   *
   * 之前没有这个字段，`catch` 只把 `loading` 复位为 false，于是
   * `if (loading || !project)` 在「拉取失败 + 无旧数据」时**永远成立**
   * → 剧集层 layout 永久转圈，用户只看到"加载中"，看不到任何报错，
   * 与真因（接口 404 / 身份不对）完全脱节。
   */
  error: string | null;
  currentEpisodeId: string | null;
  fetchProject: (
    id: string,
    episodeId?: string,
    versionId?: string,
    options?: { force?: boolean }
  ) => Promise<void>;
  updateIdea: (idea: string) => void;
  updateScript: (script: string) => void;
  setProject: (project: Project) => void;
  /** 🔴 2026-10-16：外科式局部更新（不重拉、不打断编辑）—— 见实现处注释 */
  patchProject: (fields: Partial<Project>) => void;
  /** 🔴 2026-10-16：单镜级局部更新 */
  patchShot: (shotId: string, fields: Record<string, unknown>) => void;
}

/**
 * 🔴 请求去重 + 短 TTL 缓存（2026-09-17 · 「菜单跳转非常慢」根治）。
 *
 * 改造前的问题：`fetchProject` 每调一次就发一个新请求，**什么都不判断**。
 * 而项目层 layout 与剧集层 layout **各调一次** ⇒ 每次点菜单都要下 386 + 354 = 741 KB。
 * 更糟的是快速连点菜单会叠加多个并发请求，后到的响应覆盖先到的，界面反复闪。
 *
 * 三层防护：
 *  1. `inflight` —— 相同 key 的**在途请求共享同一个 Promise**。两层 layout 同时挂载时
 *     只会发一个请求，两边都拿到同一份结果。
 *  2. `cache` —— 成功结果按 key 缓存 `CACHE_TTL_MS`。TTL 内重复访问直接命中，
 *     **零请求**。TTL 取 30s：短到不会看到明显过期数据，长到足以覆盖
 *     「在菜单间来回切换」的典型间隔。
 *  3. `force` —— 显式刷新（生成完成后、用户点刷新）绕过缓存，保证数据新鲜。
 *
 * 注意：缓存的是**原始 JSON**，不是 store 里的 project 引用。这样命中缓存时
 * 仍然走一次 `set()`，任何依赖 `project` 引用变化的 effect 行为与改造前一致。
 */
const CACHE_TTL_MS = 30_000;
const inflight = new Map<string, Promise<Project>>();
const cache = new Map<string, { data: Project; at: number }>();

/**
 * 导航序号：每发起一次 fetchProject 自增，只有「最后一次发起」的结果会被写入 store。
 * 用于丢弃乱序返回的过期响应（快速连点菜单时后发先至的旧数据不该覆盖新数据）。
 */
let reqSeq = 0;

/** 供生成完成等场景主动失效缓存（同 key 的下一次 fetch 会重新拉取）。 */
export function invalidateProjectCache(id?: string) {
  if (!id) {
    cache.clear();
    return;
  }
  for (const key of [...cache.keys()]) {
    if (key.startsWith(`${id}|`)) cache.delete(key);
  }
}

/**
 * 🔴 生成/编辑后的「强制刷新」（2026-09-18 「生成的素材没有及时更新」修复）。
 *
 * 症状：面板里生成完一张图/一段视频后，卡片上的素材**不出现**；
 * 手动切页面再切回来才看得到。极端情况下要等十几秒才自己冒出来。
 *
 * 根因链（两个缺陷叠加，缺一不可）：
 *  1. 2026-09-17 为治「菜单跳转慢」给 `fetchProject` 加了 **30s TTL 缓存**；
 *  2. 全站**没有任何调用点**传 `force: true`，`invalidateProjectCache` 也**从未被调用**
 *     （实测 grep：除定义处外 0 次引用）。
 *  于是生成完成后那句 `fetchProject(...)` 命中 30s 旧缓存，**直接 `return`，根本没发请求** ——
 *  服务端明明已经写好新素材，界面却固定拿着生成前的快照。用户感知就是「素材没更新」。
 *
 * 修法：把「生成后刷新」统一收敛到本函数 —— 先失效缓存再拉，**顺序不能反**
 * （先 fetch 会命中旧缓存，再失效就晚了）。业务代码一律用它，不要手写
 * `fetchProject(...)`（除非确实需要 TTL 缓存来省流量，如纯导航跳转）。
 *
 * @param strict 传 `true` 时直接透传 `fetchProject(..., { force: true })`，
 *               既失效缓存、又跳过 inflight 去重，用于「刚落库、必须看到最新」的场景。
 */
export function refreshProject(
  id: string,
  episodeId?: string,
  versionId?: string,
  opts?: { strict?: boolean }
): Promise<void> {
  invalidateProjectCache(id);
  const strict = opts?.strict ?? true;
  return useProjectStore
    .getState()
    .fetchProject(id, episodeId, versionId, strict ? { force: true } : undefined);
}

export const useProjectStore = create<ProjectStore>((set, get) => ({
  project: null,
  loading: false,
  refreshing: false,
  error: null,
  currentEpisodeId: null,

  fetchProject: async (
    id: string,
    episodeId?: string,
    versionId?: string,
    options?: { force?: boolean }
  ) => {
    const key = `${id}|${episodeId ?? ""}|${versionId ?? ""}`;
    const hasData = !!get().project;
    const seq = ++reqSeq;

    // 命中缓存：直接采用，不发请求。仍走 set() 以保持引用语义。
    if (!options?.force) {
      const hit = cache.get(key);
      if (hit && Date.now() - hit.at < CACHE_TTL_MS) {
        set({
          project: hit.data,
          loading: false,
          refreshing: false,
          error: null,
          currentEpisodeId: episodeId ?? null,
        });
        return;
      }
    }

    // 有旧数据时走「静默刷新」：不显示全屏 loading，只点亮 refreshing。
    // 首次加载（无数据）才显示全屏 spinner —— 与改造前语义一致。
    // 顺带清掉上一次的 error，否则重试成功后错误文案还挂着（2026-10-06）。
    if (!hasData) set({ loading: true, error: null });
    else set({ refreshing: true });

    let req = inflight.get(key);
    if (!req || options?.force) {
      // 🔴 force 必须**跳过去重**（2026-09-18 修复）：
      // 若复用在途请求，而那个在途请求是在「生成写库之前」发出的，
      // 返回的就是旧快照 —— 生成后刷新依旧看不到新素材。
      //
      // ⚠️ 时序陷阱（实测踩到）：下面的 IIFE 内含 await，**调用它就会立刻让出**，
      // 因此必须**先构造 Promise 再赋值给变量、最后再 set 进 inflight**，
      // 且 set 必须发生在任何 await 之前。写成
      //     req = (async () => { await ...; return x })();
      //     inflight.set(key, req);
      // 看似没问题，但旧请求的 finally 可能在这两行之间抢先执行，
      // 出现「旧请求以为自己不是 self 而保留、新 entry 却还没登记」的窗口，
      // 最终 inflight 里空掉 → 去重失效。
      const p = (async () => {
        const url = episodeId
          ? `/api/projects/${id}/episodes/${episodeId}${versionId ? `?versionId=${versionId}` : ""}`
          : `/api/projects/${id}${versionId ? `?versionId=${versionId}` : ""}`;
        const res = await apiFetch(url);
        return (await res.json()) as Project;
      })();
      req = p;
      inflight.set(key, p); // ← 必须在 p 被 await 之前完成登记
    }

    try {
      const data = await req;
      cache.set(key, { data, at: Date.now() });
      // 竞态保护：只接受「最后一次发起」的导航结果。
      // 用 reqSeq 自增序号判定，而不是比较 currentEpisodeId —— 后者在并发下不可靠。
      if (seq !== reqSeq) return;
      set({
        // 🔴 2026-10-16 引用稳定化：内容未变的字段/镜头复用原引用 ⇒ 轮询「无事发生」时零重渲染。
        //    见 stabilizeProject 注释（老板反复反馈的「页面自己在刷新」根因）。
        project: stabilizeProject(
          get().project as unknown as Record<string, unknown> | null,
          data as unknown as Record<string, unknown>,
        ) as unknown as Project,
        loading: false,
        refreshing: false,
        error: null,
        currentEpisodeId: episodeId ?? null,
      });
    } catch (err) {
      // 🔴 失败必须复位状态位：改造前抛错会让 loading 永远为 true，界面永久转圈。
      // 同时记下原因（2026-10-06）：之前只复位 loading，界面就退化成
      // 「一直加载中」，用户看不到任何可诊断的信息。
      const message =
        err instanceof Error ? err.message : "加载项目失败（未知错误）";
      if (seq === reqSeq) set({ loading: false, refreshing: false, error: message });
      throw err;
    } finally {
      // 🔴 只删掉「自己那一个」（2026-09-18 修复）：
      // 原实现无条件 `inflight.delete(key)`，当 force 请求覆盖了旧 entry 后，
      // 旧请求先返回就把**新请求的 entry** 删了 → 后续调用又新发一次，去重失效。
      if (inflight.get(key) === req) inflight.delete(key);
    }
  },

  updateIdea: (idea: string) => {
    set((state) => ({
      project: state.project ? { ...state.project, idea } : null,
    }));
  },

  updateScript: (script: string) => {
    set((state) => ({
      project: state.project ? { ...state.project, script } : null,
    }));
  },

  /**
   * 🔴 2026-10-16：**外科式局部更新** —— 改一两个字段不该整项目重拉。
   *
   * ## 事故（老板反馈：「前端数据刷新不要每次都全局页面刷新重新加载，会打断操作」）
   *
   * 改一个开关（如视觉增强）原本走 `fetchProject` ⇒ 两重伤害：
   *   ① **重**：整项目重拉 741KB（store 注释自记的实测值），而只改了一个布尔；
   *   ② **打断操作** —— `fetchProject` 用**全新对象**替换 `project`，
   *      凡是 `useEffect(..., [project])` 的同步（剧本页的 outline/idea 本地态就是这么同步的）
   *      都会被重新触发，**把用户正在编辑的内容覆盖掉**。开关一点、正在写的文字没了。
   *
   * ## 用法
   *
   * 只在「服务端返回值可由前端等价推出」时用（开关、纯字段编辑）；
   * 服务端会派生别的内容时（生成、抽取、重算）**仍必须重拉** —— 否则界面说谎。
   *
   * ⚠️ `shots` 未变时保留原数组引用：shots 是大数组，换引用会让所有镜头卡重渲染。
   */
  patchProject: (fields: Partial<Project>) => {
    set((state) => {
      if (!state.project) return {};
      const next = { ...state.project, ...fields };
      // shots 未出现在补丁里（或内容引用未变）⇒ 保留原引用，避免整列表重渲染
      if (!("shots" in fields)) next.shots = state.project.shots;
      return { project: next };
    });
  },

  /**
   * 🔴 2026-10-16：单镜级局部更新（同 `patchProject` 的理由）。
   * 只替换目标镜对象，其余镜保持引用不变 ⇒ 只有那一张卡重渲染。
   */
  patchShot: (shotId: string, fields: Record<string, unknown>) => {
    set((state) => {
      if (!state.project) return {};
      const shots = state.project.shots;
      const idx = shots.findIndex((s) => s.id === shotId);
      if (idx < 0) return {};
      const nextShots = shots.slice();
      nextShots[idx] = { ...shots[idx], ...fields };
      return { project: { ...state.project, shots: nextShots } };
    });
  },

  /**
   * 🔴 2026-10-16：**单镜级重拉** —— 单镜操作后不要整项目重拉。
   *
   * 事故（老板：「不要整个页面一起刷新打断操作」）：分镜页的单镜改动原本走
   * `refreshProject` ⇒ **整项目 741KB** + 新对象替换 `project` ⇒ 71 镜列表整体重渲染、
   * 抽屉/滚动/展开态全被打断。而真正变的只有一行 shot。
   *
   * 现走 `GET /api/projects/:id/shots/:shotId`（约 2–4KB）→ `patchShot` 合并
   * ⇒ **只有那一张卡重渲染**，其它镜的 DOM/状态原样保留。
   *
   * 与「整项目重拉」的判据一致：**服务端返回值能定位到单个实体时，就只更新那一个。**
   * 服务端**派生**了跨镜内容（生成整块分镜、批量抽取）时仍必须整项目重拉。
   */
  refreshShot: async (shotId: string) => {
    const st = get();
    const proj = st.project;
    if (!proj || !shotId) return;
    try {
      const res = await apiFetch(`/api/projects/${proj.id}/shots/${encodeURIComponent(shotId)}`);
      if (!res.ok) return;
      const row = (await res.json()) as Record<string, unknown>;
      if (!row || typeof row !== "object" || !("id" in row)) return;
      // 逐字段合并到目标镜（保持其它镜引用不变 ⇒ 只有这一张卡重渲染）
      get().patchShot(shotId, row);
    } catch {
      // 网络抖动忽略：不打断用户，下一次操作会自然带上最新值
    }
  },

  setProject: (project: Project) => {
    /**
     * 🔴 2026-10-25：保留 `versionStyle`。
     *
     * 它是**独立于项目主数据**的一份版本级状态（由 `VersionStyleBar` 单独拉取）。
     * 直接 `set({ project })` 会把它冲掉，于是每次项目刷新后镜头卡都退回
     * 「跟随本版：默认叙事」，而实际版本默认可能是某个风格包 ——
     * 界面与实际生效分叉，用户会以为设置丢了然后重复设置。
     */
    set((state) => ({
      project: state.project?.versionStyle && project.versionStyle === undefined
        ? { ...project, versionStyle: state.project.versionStyle }
        : project,
    }));
  },
}));
